import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Building2, CircleCheck, FileText } from 'lucide-react';
import { Card, Chip, EmptyState, MoneyText, Skeleton, StatTile } from '@/ui';
import { isCustomerOrgAdmin } from '@/auth/types';
import { useSessionStore } from '@/auth/session.store';
import { REPORT_RANGES, endOfDayIso, findReportRange, rangeStart, startOfDayIso } from '@/lib/report-range';
import { totalAmount } from '@/lib/money';
import { useUrlRange } from '@/features/reports/useUrlRange';
import { REPORT_MEMBER_TYPE_LABEL_KEYS, TASK_STATUS_LABEL_KEYS } from '@/features/reports/labels';
import {
  fetchCustomerDashboard,
  fetchCustomerMemberActivity,
  fetchCustomerOrgSummary,
} from './customer-api';
import { mergeCurrencyTotals, type CurrencyTotal } from './api';
import { customerDashboardKeys } from './query-keys';
import styles from './Dashboard.module.css';

/**
 * ====================== MUSTERI PANELI (/musteri/panel) =====================
 *
 * TEK EKRAN, IKI KAPSAM — ayrimi acikca yazmak zorundayiz, cunku ayni sayfada yan yana
 * duruyorlar ve karistirilirsa kullanici baskasinin rakamini kendi sanir:
 *
 *   1. "Benim ozetim" -> HERKESE acik, kapsam CAGIRANIN KENDI talepleri. Yonetici de
 *      burada KENDI rakamlarini gorur; org geneli ayri bolumde.
 *   2. "Organizasyon" -> YALNIZ YONETICI (sunucuda requireOrgAdmin). Personelin org
 *      toplamlarini gormesi, diger personelin hacmini dolayli olarak ifsa ederdi.
 *
 * Menude `adminOnly` YOK: personel de kendi panelini gormeli. Yonetici bolumleri
 * `isCustomerOrgAdmin` ile ekleniyor; istemci gizlemesi kolaylik, kapi sunucuda.
 *
 * RAKAMLAR GERCEK CIRO (`task_amount * offer_price`). Buro tarafindaki `offers-summary`
 * KUR TOPLAMI dondurur; markali tipler (TotalAmount / UnitRate) ikisinin karismasini
 * derleme hatasina cevirir — bkz. customer-api.ts basligi.
 *
 * CIRO BIRIM BIRIM (2026-09-23, karar Q13): EUR/USD talebinin cirosu USD, digerlerinin TRY.
 * Toplamlar para birimi basina ayri satirdir; eskiden hepsi tek bir "TRY" sayisinda
 * birlesiyordu.
 * ========================================================================== */
export function CustomerDashboardPage() {
  const { t } = useTranslation();
  const snapshot = useSessionStore((state) => state.snapshot);
  const admin = isCustomerOrgAdmin(snapshot);

  const [rangeKey, setRangeKey] = useUrlRange();
  const range = findReportRange(rangeKey);

  // Aralik SORGU ANAHTARININ PARCASI: olmasaydi aralik degisiminde TanStack ayni anahtari
  // gorup onbellekteki ESKI araligin rakamlarini gosterirdi — bir ciro ekraninda en sessiz
  // hata sinifi bu (dashboardKeys ile ayni gerekce).
  const { from, to } = useMemo(() => {
    const start = rangeStart(range);
    return {
      from: start === null ? undefined : startOfDayIso(start),
      to: endOfDayIso(new Date()),
    };
  }, [range]);

  const mine = useQuery({
    queryKey: customerDashboardKeys.mine(from ?? 'all', to ?? ''),
    queryFn: () => fetchCustomerDashboard(from, to),
  });
  const orgSummary = useQuery({
    queryKey: customerDashboardKeys.org(from ?? 'all', to ?? ''),
    queryFn: () => fetchCustomerOrgSummary(from, to),
    enabled: admin,
  });
  const memberActivity = useQuery({
    queryKey: customerDashboardKeys.members(from ?? 'all', to ?? ''),
    queryFn: () => fetchCustomerMemberActivity(from, to),
    enabled: admin,
  });

  const mineRows = mine.data ?? [];
  const orgRows = orgSummary.data ?? [];

  // useMemo YOK ve bilerek: `?? []` her render'da YENI bir dizi uretir, dolayisiyla ona
  // bagli bir memo hicbir zaman tutmaz — yalnizca tutuyormus gibi gorunurdu. Diziler
  // durum sayisi kadar (bir avuc satir); toplami dogrudan hesaplamak hem dogru hem ucuz.
  const totalsOf = (rows: readonly { taskCount: number; matchedCount: number }[]) => ({
    taskCount: rows.reduce((acc, row) => acc + row.taskCount, 0),
    matchedCount: rows.reduce((acc, row) => acc + row.matchedCount, 0),
  });
  // Ciro BIRIM BASINA toplanir; farkli birimler tek sayida birlesmez.
  const turnoverOf = (
    rows: readonly { counterCurrency: string; turnover: CurrencyTotal['total'] }[],
  ): CurrencyTotal[] =>
    mergeCurrencyTotals([rows.map((row) => ({ currency: row.counterCurrency, total: row.turnover }))]);
  const myTotals = totalsOf(mineRows);
  const myTurnover = turnoverOf(mineRows);
  const orgTotals = totalsOf(orgRows);
  const orgTurnover = turnoverOf(orgRows);

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <div>
          <h1 className={styles.title}>{t('dashboard.customer.title')}</h1>
          <p className={styles.subtitle}>
            {admin ? t('dashboard.customer.subtitleAdmin') : t('dashboard.customer.subtitleStaff')}
          </p>
        </div>
      </div>

      {/* Aralik secici ExchangeDashboardPage ile AYNI bilesenler: iki panel arasinda
          gorsel dil ayrismasin. */}
      <div className={styles.chipRow} role="group" aria-label={t('dashboard.rangeQuick')}>
        {REPORT_RANGES.map((item) => (
          <button
            key={item.key}
            type="button"
            className={[styles.rangeChip, rangeKey === item.key ? styles.rangeChipOn : undefined]
              .filter(Boolean)
              .join(' ')}
            aria-pressed={rangeKey === item.key}
            onClick={() => {
              setRangeKey(item.key);
            }}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>

      {/* ---------------------------------------------------- 1. kendi ozetim */}
      <Card padding="md" title={t('dashboard.customer.mine')}>
        {mine.isPending ? (
          <Skeleton />
        ) : mine.isError ? (
          <EmptyState tone="error" title={t('dashboard.customer.loadFailed')} />
        ) : mineRows.length === 0 ? (
          <EmptyState title={t('dashboard.customer.emptyMine')} />
        ) : (
          <>
            <div className={styles.leader}>
              <p className={styles.tileLabel}>{t('dashboard.customer.turnover')}</p>
              {withTryFallback(myTurnover).map((item) => (
                <p key={item.currency} className={styles.leaderValue}>
                  <MoneyText total={item.total} currency={item.currency} size="display" />
                </p>
              ))}
            </div>
            <div className={styles.tiles}>
              <StatTile
                label={t('dashboard.customer.taskCount')}
                value={myTotals.taskCount}
                icon={<FileText size={18} aria-hidden="true" />}
              />
              <StatTile
                label={t('dashboard.customer.matchedCount')}
                value={myTotals.matchedCount}
                icon={<CircleCheck size={18} aria-hidden="true" />}
              />
            </div>
            <ul className={styles.stackList}>
              {mineRows.map((row) => (
                <li key={`${row.statusCode}-${row.typeCode}`} className={styles.stackRow}>
                  <div className={styles.stackHead}>
                    <span className={styles.stackCode}>
                      {statusLabel(t, row.statusCode)} · {row.typeCode}
                    </span>
                    <MoneyText total={row.turnover} currency={row.counterCurrency} size="caption" />
                  </div>
                  <span className={styles.tileLabel}>
                    {t('dashboard.customer.countPair', {
                      total: row.taskCount,
                      matched: row.matchedCount,
                    })}
                  </span>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {/* ------------------------------------------- 2. organizasyon (yonetici) */}
      {admin ? (
        <>
          <Card padding="md" title={t('dashboard.customer.org')}>
            {orgSummary.isPending ? (
              <Skeleton />
            ) : orgSummary.isError ? (
              <EmptyState tone="error" title={t('dashboard.customer.loadFailed')} />
            ) : orgRows.length === 0 ? (
              <EmptyState title={t('dashboard.customer.emptyOrg')} />
            ) : (
              <>
                <div className={styles.tiles}>
                  <StatTile
                    label={t('dashboard.customer.turnover')}
                    value={<CurrencyLines totals={withTryFallback(orgTurnover)} />}
                    icon={<Building2 size={18} aria-hidden="true" />}
                  />
                  <StatTile
                    label={t('dashboard.customer.taskCount')}
                    value={orgTotals.taskCount}
                    icon={<FileText size={18} aria-hidden="true" />}
                  />
                  <StatTile
                    label={t('dashboard.customer.matchedCount')}
                    value={orgTotals.matchedCount}
                    icon={<CircleCheck size={18} aria-hidden="true" />}
                  />
                </div>
                <ul className={styles.stackList}>
                  {orgRows.map((row) => (
                    <li key={`${row.statusCode}-${row.counterCurrency}`} className={styles.stackRow}>
                      <div className={styles.stackHead}>
                        <span className={styles.stackCode}>
                          {statusLabel(t, row.statusCode)} · {row.counterCurrency}
                        </span>
                        <MoneyText total={row.turnover} currency={row.counterCurrency} size="caption" />
                      </div>
                      <span className={styles.tileLabel}>
                        {t('dashboard.customer.countPair', {
                          total: row.taskCount,
                          matched: row.matchedCount,
                        })}
                      </span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>

          <Card padding="md" title={t('dashboard.customer.memberActivity')}>
            <p className={styles.muted}>{t('dashboard.customer.memberActivityHint')}</p>
            {memberActivity.isPending ? (
              <Skeleton />
            ) : memberActivity.isError ? (
              <EmptyState tone="error" title={t('dashboard.customer.loadFailed')} />
            ) : (memberActivity.data ?? []).length === 0 ? (
              <EmptyState title={t('dashboard.customer.emptyMembers')} />
            ) : (
              <ul className={styles.stackList}>
                {(memberActivity.data ?? []).map((row) => (
                  <li key={row.appUserId} className={styles.stackRow}>
                    <div className={styles.stackHead}>
                      <span className={styles.stackCode}>
                        {row.userFullName === ''
                          ? t('dashboard.customer.unnamedMember')
                          : row.userFullName}
                      </span>
                      <CurrencyLines totals={withTryFallback(row.turnoverByCurrency)} size="caption" />
                    </div>
                    <span className={styles.tileLabel}>
                      {t('dashboard.customer.countPair', {
                        total: row.taskCount,
                        matched: row.matchedCount,
                      })}
                    </span>
                    <span className={styles.chipRow}>
                      <Chip size="sm" tone={row.memberTypeCode === 'admin' ? 'primary' : 'neutral'}>
                        {/* Uye tipi etiketleri rapor ekraniyla ORTAK: ayni kavramin iki
                            metni olsaydi biri guncellenip oteki bayat kalirdi. */}
                        {t(
                          REPORT_MEMBER_TYPE_LABEL_KEYS[row.memberTypeCode] ??
                            'report.memberStaff',
                        )}
                      </Chip>
                      {/* Uyeligi pasiflesmis personel LISTEDE KALIR: gecmis donem raporu,
                          o kisi cikarildi diye eksilmemeli. */}
                      {row.isActive ? null : (
                        <Chip size="sm" tone="warning">
                          {t('dashboard.customer.inactiveMember')}
                        </Chip>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}

/**
 * Sunucu durum kodlari SERBEST METIN olabilir; haritada yoksa kodun kendisi basilir.
 * Bos hucre, "bu durum ne" sorusunu cevapsiz birakirdi.
 */
const statusLabel = (t: (key: string) => string, code: string): string => {
  const key = TASK_STATUS_LABEL_KEYS[code];
  return key === undefined ? code : t(key);
};

/** Hic ciro yoksa bos bir alan yerine "0,00 TRY" — panelin ana birimi. */
const withTryFallback = (totals: readonly CurrencyTotal[]): readonly CurrencyTotal[] =>
  totals.length > 0 ? totals : [{ currency: 'TRY', total: totalAmount(0) }];

/** Birim basina bir satir. Birimler ASLA tek sayida toplanmaz. */
function CurrencyLines({
  totals,
  size,
}: {
  totals: readonly CurrencyTotal[];
  size?: 'caption' | undefined;
}) {
  return (
    <span className={styles.valueStack}>
      {totals.map((item) => (
        <MoneyText
          key={item.currency}
          total={item.total}
          currency={item.currency}
          {...(size === undefined ? {} : { size })}
        />
      ))}
    </span>
  );
}
